import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useKeywords } from '@/lib/hooks/use-keywords';
import { Keyword, KeywordWithSynonyms } from '@/types/custom';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Loading from '@/components/loading';

interface ContentKeywordManagerProps {
  contentId?: string;
  selectedKeywords?: string[];
  onChange?: (keywords: string[]) => void;
}

export function ContentKeywordManager({
  contentId,
  selectedKeywords,
  onChange,
}: ContentKeywordManagerProps) {
  const {
    keywords,
    getKeywordsForContent,
    addKeywordToContent,
    removeKeywordFromContent,
  } = useKeywords();
  const [contentKeywords, setContentKeywords] = useState<Keyword[]>([]);
  const [selectedKeywordId, setSelectedKeywordId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (contentId) {
      loadContentKeywords();
    } else if (selectedKeywords) {
      // If using the component in selection mode
      setContentKeywords(
        selectedKeywords.map((id) => ({
          id,
          name: keywords.find((k) => k.id === id)?.name || id,
          created_at: keywords.find((k) => k.id === id)?.created_at || '', // messy hack
          role_scope: keywords.find((k) => k.id === id)?.role_scope ?? null,
        })),
      );
      setLoading(false);
    }
  }, [contentId, selectedKeywords]);

  const loadContentKeywords = async () => {
    setLoading(true);
    const keywordsData = await getKeywordsForContent(contentId);
    setContentKeywords(keywordsData);
    setLoading(false);
  };

  const handleAddKeyword = async () => {
    if (!selectedKeywordId) return;

    if (contentId) {
      // If using with a content ID, update in database
      const success = await addKeywordToContent(contentId, selectedKeywordId);
      if (success) {
        await loadContentKeywords();
        setSelectedKeywordId('');
      }
    } else if (onChange && selectedKeywords) {
      // If using in selection mode
      const keyword = keywords.find((k) => k.id === selectedKeywordId);
      if (keyword) {
        const newKeywords = [...contentKeywords, keyword];
        setContentKeywords(newKeywords);
        onChange([...selectedKeywords, selectedKeywordId]);
        setSelectedKeywordId('');
      }
    }
  };

  const handleRemoveKeyword = async (keywordId: string) => {
    if (contentId) {
      // If using with a content ID, update in database
      const success = await removeKeywordFromContent(contentId, keywordId);
      if (success) {
        await loadContentKeywords();
      }
    } else if (onChange && selectedKeywords) {
      // If using in selection mode
      const newKeywords = contentKeywords.filter((k) => k.id !== keywordId);
      setContentKeywords(newKeywords);
      onChange(selectedKeywords.filter((id) => id !== keywordId));
    }
  };

  // Filter out keywords that are already assigned to the content
  const availableKeywords = keywords.filter(
    (keyword) => !contentKeywords.some((ck) => ck.id === keyword.id),
  );

  return (
    <div className="space-y-1" data-selected-keywords="">
      <div className="mb-1">
        <h3 className="text-sm font-medium">Keywords</h3>
      </div>

      <div className="flex flex-wrap gap-1 min-h-[32px] p-1 border rounded-md bg-white">
        {loading ? (
          <Loading />
        ) : contentKeywords.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Geen keywords toegewezen
          </p>
        ) : (
          contentKeywords.map((keyword) => (
            <Badge
              key={keyword.id}
              variant="secondary"
              className="flex items-center gap-1 py-0.5 px-1.5 text-xs"
            >
              {keyword.name}
              <button
                onClick={() => handleRemoveKeyword(keyword.id)}
                className="ml-0.5 hover:bg-muted rounded-full"
              >
                <X className="h-2 w-2" />
              </button>
            </Badge>
          ))
        )}
      </div>

      <div className="flex gap-1 items-center mt-1">
        <Select value={selectedKeywordId} onValueChange={setSelectedKeywordId}>
          <SelectTrigger className="w-[180px] h-7 text-xs">
            <SelectValue placeholder="Selecteer keyword" />
          </SelectTrigger>
          <SelectContent>
            {availableKeywords.length === 0 ? (
              <SelectItem value="no-keywords" disabled className="text-xs">
                Geen beschikbare keywords
              </SelectItem>
            ) : (
              availableKeywords.map((keyword) => (
                <SelectItem
                  key={keyword.id}
                  value={keyword.id}
                  className="text-xs"
                >
                  {keyword.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <Button
          onClick={handleAddKeyword}
          disabled={!selectedKeywordId}
          size="sm"
          className="h-7 text-xs px-3"
        >
          Toevoegen
        </Button>
      </div>
    </div>
  );
}
